/**
 * Bearer-authenticated calls to UnityApi, supplied by each site so its own API client (base URL,
 * token, retries) is reused. Paths are relative to UnityApi's root. Errors should be thrown as
 * Error instances, as their message is shown to the user.
 */
export interface UnityApiTransport {
  /** Resolves null when UnityApi returns 204 No Content. */
  get<T>(path: string): Promise<T | null>;
  post<T>(path: string, body?: unknown): Promise<T>;
}

// UnityApi's MySessionController api/my/session/agent endpoints, which bind a Viewer session
// (sessionLinkId) to the agent conversation working in it.
export function createSessionApi(unityApi: UnityApiTransport) {
  return {
    /** Records what the session is looking at (e.g. clientName/brand/caseReference) for the agent's tools. */
    async setConversationContext(sessionLinkId: string, context: Record<string, unknown>): Promise<void> {
      await unityApi.post(`api/my/session/agent/${sessionLinkId}`, context);
    },

    /** Records the latest Foundry response id of the onsite conversation bound to sessionLinkId. */
    async setLastResponse(sessionLinkId: string, conversationId: string, responseId: string): Promise<void> {
      await unityApi.post(
        `api/my/session/agent/${sessionLinkId}/response?conversationId=${encodeURIComponent(conversationId)}&responseId=${encodeURIComponent(responseId)}`,
        {},
      );
    },

    /** The latest Foundry response id of the caller's onsite conversation in sessionLinkId, if a turn has completed. */
    getLastResponse(sessionLinkId: string): Promise<string | null> {
      return unityApi.get<string>(`api/my/session/agent/${sessionLinkId}/response`);
    },

    /**
     * Resolves a Viewer session to the AgentConversationContext for conversationId (e.g. a
     * cold-opened citation link from M365/Teams). Returns the winning sessionLinkId — the
     * caller's own sessionLinkId on first link, or an earlier caller's value if the conversation
     * was already backlinked from another session/device.
     */
    backLink(sessionLinkId: string, conversationId: string): Promise<string | null> {
      return unityApi.post<string | null>(
        `api/my/session/agent/backlink?sessionLinkId=${encodeURIComponent(sessionLinkId)}&conversationId=${encodeURIComponent(conversationId)}`,
      );
    },
  };
}
